import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';

interface MovieCardProps {
  movie: Movie;
  showFavorite?: boolean;
}

export default function MovieCard({ movie, showFavorite = true }: MovieCardProps) {
  const { isFavorite, toggle } = useFavorites();
  const favorited = isFavorite(movie.id);
  const meta =
    movie.type === 'tv'
      ? `${movie.year} · ${movie.updatedTo ?? `全${movie.episodes}集`}`
      : `${movie.year} · ${movie.region}`;

  return (
    <div
      className="group relative rounded-lg overflow-hidden bg-background-100 border border-background-300/60 hover:border-primary-500/60 transition-colors"
    >
      <Link to={`/detail/${movie.id}`} className="block">
        <div className="relative w-full aspect-[2/3] overflow-hidden">
          <img
            src={movie.cover}
            alt={`${movie.title} 海报`}
            title={`${movie.title} ${movie.genres.join(' ')}`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/10 to-transparent opacity-90" />
          <div className="absolute inset-0 bg-background-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <span className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-2xl">
              <i className="ri-play-fill" />
            </span>
          </div>
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-background-950/70 backdrop-blur text-accent-400 text-xs font-semibold">
            {movie.rating.toFixed(1)}
          </span>
          <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-primary-500/90 text-background-50 text-[10px] font-medium">
            {movie.type === 'tv' ? '剧集' : '电影'}
          </span>
        </div>
      </Link>

      {showFavorite && (
        <button
          type="button"
          onClick={() => toggle(movie.id)}
          aria-label={favorited ? '取消收藏' : '收藏'}
          className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors cursor-pointer ${
            favorited
              ? 'bg-primary-500 text-background-50'
              : 'bg-background-950/60 text-foreground-900 hover:bg-primary-500 hover:text-background-50'
          }`}
        >
          <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'} />
        </button>
      )}

      <div className="p-3">
        <Link to={`/detail/${movie.id}`}>
          <h4 className="text-sm font-semibold text-foreground-950 truncate group-hover:text-primary-400 transition-colors">
            {movie.title}
          </h4>
        </Link>
        <p className="mt-1 text-xs text-foreground-500 truncate">{meta}</p>
        <p className="mt-1 text-xs text-foreground-600 truncate">
          {movie.genres.join(' / ')}
        </p>
        <p className="mt-1.5 text-[11px] text-foreground-500 flex items-center gap-1">
          <i className="ri-history-line" />
          更新 {movie.updateTime}
        </p>
      </div>
    </div>
  );
}